/**
 * Default size selection on the PDP.
 *
 * Shopify defaults a product page to `first_available_variant`, which is the first
 * value in the admin's option order — the smallest size. On the Compression Knee
 * Sleeve that means the page loads on Youth, which is 3% of that product's units.
 *
 * Sizes must stay displayed smallest-to-largest (Y, S, M, L, XL, XXL), so this
 * changes the *selection* only, never the order. On a cold PDP load with no
 * `?variant=` in the URL, we pick the best-selling in-stock size and click its
 * radio. That is the exact same code path as a shopper clicking it, so the
 * Section Rendering fetch, the price/inventory/media morph, the hidden variantId
 * input, and any app listening for a variant change (Kaching Bundles, the sticky
 * add-to-cart bar) all update the way they already do.
 *
 * Preference order is demand-ranked from 120 days of variantPerformance:
 * L 29.5% > M 19.6% > XL 19.5% > XXL 9.6% > S 6.2% > Y 0.9%.
 */

const SIZE_PREFERENCE = [
  'l',
  'large',
  'l/xl',
  'm',
  'medium',
  'm/l',
  'xl',
  'x-large',
  'xl/xxl',
  'xxl',
  'xx-large',
  '2xl',
  'xxxl',
  '3xl',
  '4xl',
  's/m',
  's',
  'small',
  'xs',
  'xxs',
  'y',
  'youth',
];

const normalize = (value) => String(value ?? '').trim().toLowerCase();

const rankOf = (value) => {
  const index = SIZE_PREFERENCE.indexOf(normalize(value));
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

/**
 * Picks the size to preselect.
 *
 * `preferred` is this product's own best-selling size, written to the
 * `custom.default_size` metafield from Olympus sales data. It wins whenever it is
 * in stock — the catalog-wide ladder is only the fallback for products with no
 * metafield (new or low-volume) or whose best seller has sold out.
 *
 * @param {{value: string, available: boolean, selected: boolean}[]} candidates
 * @param {string} [preferred] - This product's best-selling size.
 * @returns {number} Index of the winner, or -1 to leave the selection alone.
 */
function pickBestIndex(candidates, preferred) {
  if (preferred) {
    const wanted = candidates.findIndex(
      (candidate) => candidate.available && normalize(candidate.value) === normalize(preferred)
    );
    if (wanted !== -1) return candidates[wanted].selected ? -1 : wanted;
  }

  let bestIndex = -1;
  let bestRank = Number.POSITIVE_INFINITY;

  candidates.forEach((candidate, index) => {
    if (!candidate.available) return;
    const rank = rankOf(candidate.value);
    if (rank < bestRank) {
      bestRank = rank;
      bestIndex = index;
    }
  });

  // Every size is off the preference list (sticker "6 Inch", mug "16 oz") — leave it alone.
  if (bestRank === Number.POSITIVE_INFINITY) return -1;

  // Already on the best size, or the shopper's current pick ranks just as well.
  const current = candidates.findIndex((candidate) => candidate.selected);
  if (current !== -1 && rankOf(candidates[current].value) <= bestRank) return -1;

  return bestIndex;
}

function applyDefaultSize() {
  // Only the main PDP picker. Product cards and quick-add dialogs keep Shopify's default.
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!picker) return;

  const preferred = picker.dataset.defaultSize;
  const buttons = picker.querySelector('fieldset[data-option-name="Size" i]');
  const dropdown = picker.querySelector('.variant-option--dropdowns[data-option-name="Size" i] select');

  if (buttons) {
    const radios = /** @type {HTMLInputElement[]} */ (Array.from(buttons.querySelectorAll('input[type="radio"]')));
    const best = pickBestIndex(
      radios.map((radio) => ({
        value: radio.value,
        available: radio.dataset.optionAvailable !== 'false',
        selected: radio.checked,
      })),
      preferred
    );

    if (best !== -1) radios[best].click();
    return;
  }

  if (dropdown) {
    const options = Array.from(dropdown.options);
    const best = pickBestIndex(
      options.map((option) => ({
        value: option.value,
        // The dropdown marks sold-out values in the label rather than a data attribute.
        available: !/unavailable|sold out/i.test(option.textContent ?? ''),
        selected: option.selected,
      })),
      preferred
    );

    if (best === -1) return;
    dropdown.value = options[best].value;
    dropdown.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// A `?variant=` in the URL is an explicit choice — a shared link, an ad landing page,
// a back-button restore. Never override it.
if (!new URL(window.location.href).searchParams.has('variant')) {
  customElements.whenDefined('variant-picker').then(() => {
    // Wait one frame so the picker's connectedCallback has indexed its radios.
    requestAnimationFrame(applyDefaultSize);
  });
}
